import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaPhone } from 'react-icons/fa'; // Import phone icon
import heroBg from '../assets/hero-bg.jpg'; // Import the hero background image
import beforeImage from '../assets/before.jpg'; // Import before image
import afterImage from '../assets/after.jpg'; // Import after image
import commercialClient1 from '../assets/commercial-client1-new.png';
import commercialClient2 from '../assets/commercial-client2.jpg'; // Import commercial client image 2
import windowCleaningImage from '../assets/IMG_5326.HEIC.png';
import gutterCleaningImage from '../assets/gutter-cleaning4.jpg'; // Placeholder image
import polymericSandingImage from '../assets/polymeric-sanding-new.jpg';
import houseWashingImage from '../assets/house-washing-new.webp';
import pressureWashingImage from '../assets/pressure-washing-new.png';
import SEO from '../components/SEO';
import './Home.css';

function Home() {
    const [currentReview, setCurrentReview] = useState(0);
    const [autoplay, setAutoplay] = useState(true);

    const reviews = [
        {
            text: "Fast and efficient!! Windows look fantastic.",
            author: "Sherri Johnston",
        },
        {
            text: "Very courteous and efficient. Showed up on proposed time and provide window cleaning without wasting time.",
            author: "Mike Fox",
        },
        {
            text: "Excellent service with no disruption to our routine. Highly recommended.",
            author: "Edward Thompson",
        },
        {
            text: "Did a great job on the windows, were on time, prepared, pleasant, and careful around the garden plants. Thank you, Kyle and team!",
            author: "Vicki Young",
        },
        {
            text: "Hired this company to do our windows not knowing anything about their history. They were fantastic. From the moment they arrived they were professional, explained the process, and left the windows looking great.",
            author: "Jim Chessie",
        },
        {
            text: "I don't usually post reviews but this company deserves it. The entire process was seamless, the crew was polite and careful, and my windows are sparkling like they are brand new.",
            author: "Jayne",
        },
        {
            text: "Kyle and his staff were very professional and did an excellent job cleaning our gutters and windows. I would highly recommend them and plan to book them again next year!",
            author: "Anne Stafford",
        },
        {
            text: "Excellent service! Great job! Highly recommend Kyle and his awesome employees.",
            author: "Nancy Waite",
        },
    ];

    // Auto-rotate reviews every 5 seconds
    useEffect(() => {
        let interval;
        if (autoplay) {
            interval = setInterval(() => {
                setCurrentReview((prev) => (prev + 1) % reviews.length);
            }, 5000);
        }
        return () => clearInterval(interval);
    }, [autoplay, reviews.length]);

    const nextReview = () => {
        setAutoplay(false); // Stop autoplay when user interacts
        setCurrentReview((prev) => (prev + 1) % reviews.length);
    };

    const prevReview = () => {
        setAutoplay(false); // Stop autoplay when user interacts
        setCurrentReview((prev) => (prev - 1 + reviews.length) % reviews.length);
    };

    return (
        <div className="home">
            <SEO
                title="Window Cleaning Fredericton, NB | Gutter Cleaning | Prestige Clean"
                description="Professional window cleaning and gutter cleaning in Fredericton, NB for homes and businesses. Also offering house washing, pressure washing, and polymeric sanding. Free quotes."
                keywords="window cleaning fredericton, gutter cleaning fredericton, house washing fredericton, pressure washing fredericton, polymeric sanding fredericton, fredericton window cleaning, fredericton gutter cleaning, residential window cleaning fredericton, commercial window cleaning fredericton, exterior cleaning fredericton"
                canonicalUrl="https://prestigecleanhomeservices.ca/"
            />

            {/* Hero Section */}
            <section className="hero" style={{ backgroundImage: `url(${heroBg})` }}>
                <div className="hero-overlay"></div>
                <div className="hero-content">
                    <h1>Professional Window Cleaning in Fredericton, NB</h1>
                    <p className="hero-lead">Window Cleaning • Gutter Cleaning • House Washing • Pressure Washing • Polymeric Sanding</p>
                    <p className="hero-proof">5-star rated by Fredericton homeowners and businesses.</p>
                    <div className="hero-buttons">
                        <Link to="/contact#contact-form" className="cta-button">Get a Free Quote</Link>
                        <a href="tel:5064716129" className="secondary-button"><FaPhone className="phone-icon" />(506) 471-6129</a>
                    </div>
                </div>
            </section>

            {/* About Us Section */}
            <section className="about-us">
                <div className="about-container">
                    <div className="about-content">
                        <h2>Meet the Owner</h2>
                        <p>
                            Prestige Clean is a locally owned and operated business in Fredericton, New Brunswick founded by Kyle DeMerchant in 2023. What started as a weekend venture helping neighbors with exterior window cleaning has grown into a full-scale operation, serving both residential and commercial properties.
                        </p>
                        <p>
                            We take pride in delivering top-quality service, ensuring your property looks its best from top to bottom. In addition to window cleaning, we also offer professional gutter cleaning, house washing, pressure washing, and polymeric sanding to help maintain and enhance your property.
                        </p>
                        <div className="about-details">
                            <div className="about-item">
                                <h3>Our Mission</h3>
                                <p>
                                    To deliver reliable, efficient, and eco-friendly cleaning solutions that
                                    enhance the quality of life for our clients.
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="about-video">
                        <iframe
                            src="https://www.youtube.com/embed/1kB3frEd90g"
                            title="Meet Kyle - Prestige Clean"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        ></iframe>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="services" id="services">
                <div className="container">
                    <h2>Our Services</h2>
                    <div className="services-container">
                        <div className="service-item primary-service">
                            <div className="service-image-container">
                                <img src={windowCleaningImage} alt="Professional residential window cleaning service around Fredericton" className="service-image" />
                                <div className="service-overlay">
                                    <Link to="/services#window-cleaning" className="learn-more-btn">Learn More</Link>
                                </div>
                            </div>
                            <div className="service-content">
                                <h3>Window Cleaning</h3>
                                <p>
                                    Our window cleaning services leave your glass streak-free, frames clean, and tracks cleared, enhancing your home's appearance inside and out.
                                </p>
                                <Link to="/contact#contact-form" className="cta-button">Get a Quote</Link>
                            </div>
                        </div>
                        <div className="service-item">
                            <div className="service-image-container">
                                <img src={gutterCleaningImage} alt="Expert gutter cleaning service for homes in Fredericton" className="service-image" />
                                <div className="service-overlay">
                                    <Link to="/services#gutter-cleaning" className="learn-more-btn">Learn More</Link>
                                </div>
                            </div>
                            <div className="service-content">
                                <h3>Gutter Cleaning</h3>
                                <p>
                                    Keep your gutters clean and functional with our expert gutter cleaning services.
                                    Prevent water damage and maintain your home's integrity.
                                </p>
                                <Link to="/contact#contact-form" className="cta-button">Get a Quote</Link>
                            </div>
                        </div>
                        <div className="service-item">
                            <div className="service-image-container">
                                <img src={polymericSandingImage} alt="Durable polymeric sanding for pavers in Fredericton" className="service-image" />
                                <div className="service-overlay">
                                    <Link to="/services#polymeric-sanding" className="learn-more-btn">Learn More</Link>
                                </div>
                            </div>
                            <div className="service-content">
                                <h3>Polymeric Sanding</h3>
                                <p>
                                    Our polymeric sanding services ensure your pavers are stable, durable, and
                                    weed-free, leaving your outdoor spaces looking pristine.
                                </p>
                                <Link to="/contact#contact-form" className="cta-button">Get a Quote</Link>
                            </div>
                        </div>
                        <div className="service-item">
                            <div className="service-image-container">
                                <img src={houseWashingImage} alt="Professional house washing service in Fredericton" className="service-image" />
                                <div className="service-overlay">
                                    <Link to="/services#house-washing" className="learn-more-btn">Learn More</Link>
                                </div>
                            </div>
                            <div className="service-content">
                                <h3>House Washing</h3>
                                <p>
                                    Remove built-up dirt, algae, and grime from your siding with a thorough house washing service that refreshes your home's curb appeal.
                                </p>
                                <Link to="/contact#contact-form" className="cta-button">Get a Quote</Link>
                            </div>
                        </div>
                        <div className="service-item">
                            <div className="service-image-container">
                                <img src={pressureWashingImage} alt="Exterior pressure washing service in Fredericton" className="service-image" />
                                <div className="service-overlay">
                                    <Link to="/services#pressure-washing" className="learn-more-btn">Learn More</Link>
                                </div>
                            </div>
                            <div className="service-content">
                                <h3>Pressure Washing</h3>
                                <p>
                                    Restore driveways, walkways, patios, and other hard surfaces with high-impact pressure washing that cuts through stubborn buildup.
                                </p>
                                <Link to="/contact#contact-form" className="cta-button">Get a Quote</Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Before and After Section */}
            <section className="before-after">
                <div className="container">
                    <h2>The Prestige Clean Difference</h2>
                    <p className="section-intro">See the dramatic difference our professional window cleaning makes</p>
                    <div className="before-after-container">
                        <div className="before-after-image">
                            <img src={beforeImage} alt="Before Window Cleaning" />
                            <div className="image-label">Before</div>
                        </div>
                        <div className="before-after-image">
                            <img src={afterImage} alt="After Window Cleaning" />
                            <div className="image-label">After</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Commercial Work Section */}
            <section className="commercial-work">
                <div className="container">
                    <h2>Commercial Window Cleaning</h2>
                    <p className="section-intro">Helping businesses throughout Fredericton maintain cleaner, brighter, more professional-looking windows</p>
                    <div className="commercial-container">
                        <div className="commercial-image">
                            <img src={commercialClient1} alt="Commercial Window Cleaning Project" />
                        </div>
                        <div className="commercial-image">
                            <img src={commercialClient2} alt="Commercial Window Cleaning Service" />
                        </div>
                    </div>
                    <p className="commercial-text">
                        We specialize in commercial window cleaning services for storefronts, offices, apartment buildings, and other business properties. Our team delivers streak-free results that help your property look polished and professional, with dependable service for projects of all sizes.
                    </p>
                    <Link to="/contact#contact-form" className="cta-button">Request Commercial Window Cleaning</Link>
                </div>
            </section>

            {/* Reviews Section */}
            <section className="reviews">
                <div className="container">
                    <h2>What Our Clients Say</h2>
                    <p className="section-intro">Recent Google reviews from homeowners and businesses who booked Prestige Clean.</p>
                    <div className="reviews-carousel">
                        <button className="carousel-arrow left" onClick={prevReview} aria-label="Previous review">
                            &lt;
                        </button>
                        <div className="reviews-container">
                            <div className="review-item">
                                <div className="review-content">
                                    <span className="quote-mark">"</span>
                                    <p className="review-text">{reviews[currentReview].text}</p>
                                    <span className="quote-mark closing">"</span>
                                </div>
                                <p className="review-author">- {reviews[currentReview].author}</p>
                                <div className="review-indicator">
                                    {reviews.map((_, index) => (
                                        <span
                                            key={index}
                                            className={`indicator-dot ${index === currentReview ? 'active' : ''}`}
                                            onClick={() => {
                                                setAutoplay(false);
                                                setCurrentReview(index);
                                            }}
                                        ></span>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <button className="carousel-arrow right" onClick={nextReview} aria-label="Next review">
                            &gt;
                        </button>
                    </div>
                </div>
            </section>

        </div>
    );
}

export default Home;
